import {
  Box,
  HStack,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { graphql, usePaginationFragment } from "react-relay";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { OptionTypeBase } from "react-select";

import { UrlMappingMatchType } from "~/src/__generated__/schema";
import { UrlMappingListTab_duplicateMutation } from "~/src/__relay_artifacts__/UrlMappingListTab_duplicateMutation.graphql";
import { UrlMappingListTab_user$key } from "~/src/__relay_artifacts__/UrlMappingListTab_user.graphql";
import { OutlineButton, SolidButton } from "~/src/components/common/Button";
import { Select } from "~/src/components/common/Select";
import {
  Table,
  TableHeaderColumn,
  TableHeaderRow,
} from "~/src/components/common/tables/Table";
import {
  TablePagination,
  useTablePaginationProps,
} from "~/src/components/common/tables/TablePagination";
import { TableSearchField } from "~/src/components/common/tables/TableSearchField";
import { useMutationCommit } from "~/src/lib/react-relay";
import { useUpdateDebounce } from "~/src/lib/react-use";

import { matchTypeOptions } from "../../UrlMappingForm/urlMappingFormUtils";

import { UrlMappingCacheStatus } from "./UrlMappingCacheStatus";
import { UrlMappingCsvUploadModal } from "./UrlMappingCsvUploadModal";
import {
  UrlMappingTableDataRow,
  Props as UrlMappingTableDataRowProps,
} from "./UrlMappingTableDataRow";
import { UrlMappingTestModal } from "./UrlMappingTestModal";

const greenOutlineSx = {
  color: "#38A169",
  borderColor: "#38A169",
  _hover: { color: "#2F855A", borderColor: "#2F855A" },
  _active: { color: "#276749", borderColor: "#276749" },
};

type ActiveFilter = "all" | "active" | "inactive";
type MatchTypeFilter = UrlMappingMatchType | "all";

type ListFilterVariables = {
  searchTerm: string | null;
  urlSearch: string | null;
  active: boolean | null;
  matchType: UrlMappingMatchType | null;
};

const activeFilterOptions: { value: ActiveFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "active", label: "有効のみ" },
  { value: "inactive", label: "無効のみ" },
];

const matchTypeFilterOptions: { value: MatchTypeFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  ...matchTypeOptions.map(({ value, label }) => ({
    value: value as MatchTypeFilter,
    label,
  })),
];

const buildListFilterVariables = (
  searchTerm: string,
  urlSearch: string,
  activeFilter: ActiveFilter,
  matchTypeFilter: MatchTypeFilter
): ListFilterVariables => ({
  searchTerm: searchTerm || null,
  urlSearch: urlSearch || null,
  active:
    activeFilter === "active"
      ? true
      : activeFilter === "inactive"
      ? false
      : null,
  matchType: matchTypeFilter === "all" ? null : matchTypeFilter,
});

export type Props = {
  userRef: UrlMappingListTab_user$key;
  siteSlug: string;
  onDeleteClick: UrlMappingTableDataRowProps["onDeleteClick"];
  onActiveChange: UrlMappingTableDataRowProps["onActiveChange"];
};

type UrlMappingListTabLocationState = {
  refetchUrlMappingList?: boolean;
};

const userFragment = graphql`
  fragment UrlMappingListTab_user on User
  @refetchable(queryName: "UrlMappingListTab_Query")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 120 }
    cursor: { type: "String" }
    searchTerm: { type: "String" }
    urlSearch: { type: "String" }
    active: { type: "Boolean" }
    matchType: { type: "UrlMappingMatchType" }
  ) {
    urlMappings(
      first: $count
      after: $cursor
      searchTerm: $searchTerm
      urlSearch: $urlSearch
      active: $active
      matchType: $matchType
    ) @connection(key: "UrlMappingListTab_urlMappings") {
      __id
      totalCount
      edges {
        node {
          id
          ...UrlMappingTableDataRow_urlMapping
        }
      }
    }
    ...UrlMappingCacheStatus_user
  }
`;

const duplicateMutation = graphql`
  mutation UrlMappingListTab_duplicateMutation(
    $input: DuplicateUrlMappingInput!
  ) {
    duplicateUrlMapping(input: $input) {
      urlMapping {
        id
      }
    }
  }
`;

export const UrlMappingListTab: FC<Props> = ({
  userRef,
  siteSlug,
  onDeleteClick,
  onActiveChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const duplicateMutate =
    useMutationCommit<UrlMappingListTab_duplicateMutation>(duplicateMutation);
  const [searchTerm, setSearchTerm] = useState("");
  const [urlSearch, setUrlSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [matchTypeFilter, setMatchTypeFilter] =
    useState<MatchTypeFilter>("all");
  const {
    isOpen: isTestModalOpen,
    onOpen: onTestModalOpen,
    onClose: onTestModalClose,
  } = useDisclosure();
  const {
    isOpen: isCsvUploadOpen,
    onOpen: onCsvUploadOpen,
    onClose: onCsvUploadClose,
  } = useDisclosure();

  const {
    data: user,
    loadNext,
    hasNext,
    refetch,
  } = usePaginationFragment(userFragment, userRef);

  const filterVariables = useMemo(
    () =>
      buildListFilterVariables(
        searchTerm,
        urlSearch,
        activeFilter,
        matchTypeFilter
      ),
    [searchTerm, urlSearch, activeFilter, matchTypeFilter]
  );

  useEffect(() => {
    const state = location.state as
      | UrlMappingListTabLocationState
      | null
      | undefined;
    if (!state?.refetchUrlMappingList) return;

    let cancelled = false;
    refetch(filterVariables, {
      fetchPolicy: "network-only",
      onComplete: () => {
        if (cancelled) return;
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: {} }
        );
      },
    });

    return () => {
      cancelled = true;
    };
  }, [
    filterVariables,
    location.pathname,
    location.search,
    location.state,
    navigate,
    refetch,
  ]);

  const { tablePaginationProps } = useTablePaginationProps({
    totalCount: user.urlMappings.totalCount,
    hasNext,
    loadNext,
    refetch,
  });

  const rows = useMemo(() => {
    const edges = user.urlMappings.edges || [];
    const from = tablePaginationProps.from;
    const to = tablePaginationProps.to;
    return edges.slice(from, to).map((edge) => {
      const node = edge?.node;
      if (!node) throw new Error("assertion error");
      return node;
    });
  }, [
    user.urlMappings.edges,
    tablePaginationProps.from,
    tablePaginationProps.to,
  ]);

  useUpdateDebounce(
    () => {
      refetch(filterVariables);
    },
    500,
    [refetch, filterVariables]
  );

  const handleImportSuccess = useCallback(() => {
    void refetch(filterVariables);
  }, [filterVariables, refetch]);

  const handleMatchTypeFilterChange = useCallback(
    (option: OptionTypeBase | null) => {
      const value = option?.value as MatchTypeFilter | undefined;
      setMatchTypeFilter(value ?? "all");
    },
    []
  );

  const handleActiveFilterChange = useCallback(
    (option: OptionTypeBase | null) => {
      const value = option?.value as ActiveFilter | undefined;
      setActiveFilter(value ?? "all");
    },
    []
  );

  const handleDuplicateClick = useCallback<
    UrlMappingTableDataRowProps["onDuplicateClick"]
  >(
    async (urlMappingId) => {
      try {
        const result = await duplicateMutate({
          variables: {
            input: { urlMappingId },
          },
        });
        if (!result.duplicateUrlMapping?.urlMapping) {
          throw new Error("assertion failed");
        }

        await new Promise<void>((resolve, reject) => {
          refetch(filterVariables, {
            fetchPolicy: "network-only",
            onComplete: (error) => {
              if (error) reject(error);
              else resolve();
            },
          });
        });
        toast({
          title: "URLマッピングをコピーしました（無効）",
          status: "success",
        });
      } catch {
        toast({
          title: "コピーに失敗しました",
          status: "error",
        });
      }
    },
    [duplicateMutate, filterVariables, refetch, toast]
  );

  return (
    <Box my="16px">
      <UrlMappingCacheStatus userRef={user} />
      <HStack justifyContent="flex-end" mb="16px" spacing="16px">
        <OutlineButton onClick={onTestModalOpen}>URLテスト</OutlineButton>
        <OutlineButton sx={greenOutlineSx} onClick={onCsvUploadOpen}>
          CSVアップロード
        </OutlineButton>
        <NavLink to={`/sites/${siteSlug}/url_mappings/new`}>
          <SolidButton>登録</SolidButton>
        </NavLink>
      </HStack>
      <VStack align="stretch" spacing="12px" mb="16px">
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="12px">
          <Box>
            <Text fontSize="sm" mb="4px">
              名称で検索
            </Text>
            <TableSearchField
              searchText={searchTerm}
              onChange={setSearchTerm}
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb="4px">
              URL・条件で検索
            </Text>
            <TableSearchField searchText={urlSearch} onChange={setUrlSearch} />
            <Text fontSize="xs" color="gray.500" mt="4px">
              ソース / 宛先 / ホスト / パス / 正規表現 / 追加条件
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" mb="4px">
              マッチ種別
            </Text>
            <Select
              value={matchTypeFilterOptions.find(
                (option) => option.value === matchTypeFilter
              )}
              options={matchTypeFilterOptions}
              onChange={handleMatchTypeFilterChange}
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb="4px">
              有効
            </Text>
            <Select
              value={activeFilterOptions.find(
                (option) => option.value === activeFilter
              )}
              options={activeFilterOptions}
              onChange={handleActiveFilterChange}
            />
          </Box>
        </SimpleGrid>
      </VStack>
      <UrlMappingTestModal
        isOpen={isTestModalOpen}
        onClose={onTestModalClose}
        siteSlug={siteSlug}
      />
      <UrlMappingCsvUploadModal
        isOpen={isCsvUploadOpen}
        onClose={onCsvUploadClose}
        siteSlug={siteSlug}
        onImportSuccess={handleImportSuccess}
      />
      <Table>
        <TableHeaderRow>
          <TableHeaderColumn>名称</TableHeaderColumn>
          <TableHeaderColumn>有効</TableHeaderColumn>
          <TableHeaderColumn>マッチ種別</TableHeaderColumn>
          <TableHeaderColumn>優先度</TableHeaderColumn>
          <TableHeaderColumn>ソース</TableHeaderColumn>
          <TableHeaderColumn>宛先</TableHeaderColumn>
          <TableHeaderColumn>操作</TableHeaderColumn>
        </TableHeaderRow>
        {rows.map((row) => (
          <UrlMappingTableDataRow
            key={row.id}
            urlMappingRef={row}
            siteSlug={siteSlug}
            connectionId={user.urlMappings.__id}
            onDeleteClick={onDeleteClick}
            onActiveChange={onActiveChange}
            onDuplicateClick={handleDuplicateClick}
          />
        ))}
      </Table>
      <TablePagination {...tablePaginationProps} />
    </Box>
  );
};
